import _ from 'lodash';
import moment from 'moment';
import Model from '@/types/models/';
import { loadStatus } from '@/types';

import LoadGroupMixin from './loadGroupMixin';
import LoadTransactionsMixin from './loadTransactionsMixin';

export default {
  mixins: [
    LoadGroupMixin,
    LoadTransactionsMixin,
  ],
  data() {
    return {
      sortValue: null,
      search: null,
    };
  },
  computed: {
    loadModel() {
      return Model.load;
    },
    loadStatus() {
      return loadStatus;
    },
    scheduledLoads() {
      return this.sortLoads(this.bookedLoads.filter(bookedLoad =>
        bookedLoad.loadStatus === loadStatus.SCHEDULED && this.filterLoad(bookedLoad)),
      this.sortValue);
    },
    inTransitLoads() {
      return this.sortLoads(this.bookedLoads.filter(bookedLoad =>
        bookedLoad.loadStatus === loadStatus.IN_TRANSIT && this.filterLoad(bookedLoad)),
      this.sortValue);
    },
    deliveredLoads() {
      return this.sortLoads(this.bookedLoads.filter(bookedLoad =>
        bookedLoad.loadStatus === loadStatus.DELIVERED && this.filterLoad(bookedLoad)),
      this.sortValue);
    },
  },
  watch: {
    sortValue(val) {
      if (val) {
        localStorage.setItem('loadSorting', val);
      } else {
        localStorage.removeItem('loadSorting');
      }
    },
  },
  mounted() {
    const savedValue = localStorage.getItem('loadSorting');
    if (savedValue) {
      this.sortValue = savedValue;
    }
  },
  methods: {
    sortLoads(items, field) {
      let sortedItems;
      switch (field) {
        case 'pickup_asc':
          sortedItems = _.orderBy(items, [load =>
            moment(Model.load.notificationPickupTime(load).dateObject,
              moment.ISO_8601).unix()], ['asc']);
          break;
        case 'pickup_desc':
          sortedItems = _.orderBy(items, [load =>
            moment(Model.load.notificationPickupTime(load).dateObject,
              moment.ISO_8601).unix()], ['desc']);
          break;
        case 'driver_asc':
          sortedItems = _.orderBy(items, [load => Model.load.assignedTruckingCompanyDriverName(load) || ''], ['asc']);
          break;
        case 'driver_desc':
          sortedItems = _.orderBy(items, [load => Model.load.assignedTruckingCompanyDriverName(load) || ''], ['desc']);
          break;
        case 'dispatcher_asc':
          sortedItems = _.orderBy(items, [load => load.ownerCompanyName || ''], ['asc']);
          break;
        case 'dispatcher_desc':
          sortedItems = _.orderBy(items, [load => load.ownerCompanyName || ''], ['desc']);
          break;
        default:
          sortedItems = items;
      }

      return sortedItems;
    },
    // this is for BookedLoad V1 API object
    filterLoad(load) {
      return this.filterLoadLegacy(load);
    },
    filterLoadLegacy(load) {
      return !this.search ||
        this.filter(load.humanId, this.search) ||
        this.filter(this.loadModel.billOfLadingNumber(load), this.search) ||
        this.filter(this.loadModel.listingCompany(load).name, this.search) ||
        this.filter(this.loadModel.haulingCompany(load).name, this.search) ||
        this.filter(this.loadModel.pickupCompany(load).name, this.search) ||
        this.filter(this.loadModel.pickupLocationChute(load).companyName, this.search) ||
        this.filter(this.loadModel.pickupLocationChute(load).label, this.search) ||
        this.filter(this.loadModel.pickupLocationGooglePlace(load).placeName, this.search) ||
        this.filter(this.loadModel.pickupLocationGooglePlace(load).formattedAddress, this.search) ||
        this.filter(this.loadModel.dropoffCompany(load).name, this.search) ||
        this.filter(this.loadModel.dropoffLocationChute(load).companyName, this.search) ||
        this.filter(this.loadModel.dropoffLocationChute(load).label, this.search) ||
        this.filter(this.loadModel.dropoffLocationGooglePlace(load).placeName, this.search) ||
        this.filter(this.loadModel.dropoffLocationGooglePlace(load).formattedAddress,
          this.search) ||
        this.filter(this.loadModel.assignedTruckingCompanyDriverName(load), this.search);
    },
    filter(val, search) {
      if (val === undefined) {
        return false;
      }

      return val != null && typeof val !== 'boolean' && val.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1;
    },
    filterLoadGroup(loadGroup) {
      const load = loadGroup.loadGroup || loadGroup;

      const pickupCompany = this.loadGroupShipper(load) || {};
      const pickupChute = this.loadGroupPickupLocationChute(load);
      const pickupLocationGooglePlace = this.loadGroupPickupLocationGooglePlace(load);

      const dropoffCompany = this.loadGroupReceiver(load) || {};
      const dropoffChute = this.loadGroupDropoffLocationChute(load);
      const dropoffLocationGooglePlace = this.loadGroupDropoffLocationGooglePlace(load);

      return !this.search ||
        this.filter(load.humanId, this.search) ||
        this.filter(this.loadGroupNickname(load), this.search) ||

        this.filter(this.loadGroupCustomerCompanyName(load), this.search) ||
        this.filter(this.loadGroupOwnerName(load), this.search) ||

        this.filter(pickupCompany.name, this.search) ||
        this.filter(pickupChute.companyName, this.search) ||
        this.filter(pickupChute.label, this.search) ||
        this.filter(pickupLocationGooglePlace.placeName, this.search) ||
        this.filter(pickupLocationGooglePlace.region, this.search) ||
        this.filter(pickupLocationGooglePlace.formattedAddress, this.search) ||

        this.filter(dropoffCompany.name, this.search) ||
        this.filter(dropoffChute.companyName, this.search) ||
        this.filter(dropoffChute.label, this.search) ||
        this.filter(dropoffLocationGooglePlace.placeName, this.search) ||
        this.filter(dropoffLocationGooglePlace.region, this.search) ||
        this.filter(dropoffLocationGooglePlace.formattedAddress, this.search) ||
        false;
    },
    // this is for BookedLoad V2 API object, including QB vendor assignments
    filterBookedLoad(load) {
      const group = load.tacticalInfo.loadGroup;

      return !this.search ||
        this.filter(load.humanId, this.search) ||
        this.filter(this.bookedLoadNickname(load), this.search) ||
        this.filter(this.bookedLoadBillOfLadingNumber(load), this.search) ||
        this.filter(this.bookedLoadHaulingCompany(load).name, this.search) ||
        this.filter(this.bookedLoadAssignedTruckingCompanyDriverName(load), this.search) ||

        (group && this.filterLoadGroup(group));
    },
    // NOT for settlement objects found on V1 booked load
    filterLoadTransaction(txn) {
      if (!txn) {
        return false;
      }

      const load = txn.load;
      const invoice = this.transactionInvoice(txn);
      const payment = this.transactionPayment(txn);
      const receivedPayment = this.transactionReceivedPayment(txn);

      return !this.search ||
        this.filter(txn.id, this.search) ||
        this.filter(txn.billOfLadingNumber, this.search) ||
        this.filter(txn.amount, this.search) ||
        this.filter(this.transactionDescription(txn), this.search) ||
        this.filter(invoice.number, this.search) ||
        this.filter(invoice.createdBy, this.search) ||
        this.filter(payment.number, this.search) ||
        this.filter(payment.createdBy, this.search) ||
        this.filter(receivedPayment.number, this.search) ||
        this.filter(receivedPayment.createdBy, this.search) ||

        this.filterBookedLoad(load); // this will filter the load group too
    },
    filterDispatchRequest(dr) {
      return this.filterLoadGroup(dr) ||
        this.filter(dr.humanId, this.search) ||
        this.filter(dr.dispatcher.companyName, this.search);
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/filterLoads.js